/**
 * ============================================================================
 * useDeviationsQuery — Phase 7 frontend (Deviation Management Module).
 * Owner: Phase 7 frontend agent.
 * Purpose: TanStack Query hook for the QA-Manager triage queue. Returns the
 *          deviation list filtered by classification / status / source area.
 * Endpoint: GET /api/v1/deviations.
 *
 * URS rows: DEV-002 (triage queue), DEV-004 (classification filter).
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export type DeviationClassification = 'CRITICAL' | 'MAJOR' | 'MINOR';

export type DeviationStatus =
  | 'REPORTED'
  | 'TRIAGED'
  | 'INVESTIGATION'
  | 'RCA'
  | 'CLOSED'
  | 'CANCELLED';

export type SourceArea =
  | 'MANUFACTURING'
  | 'PACKAGING'
  | 'WAREHOUSE'
  | 'LAB'
  | 'UTILITIES'
  | 'QA'
  | 'IT'
  | 'OTHER';

export interface DeviationListItem {
  deviationId: string;
  deviationNumber: string;
  title: string;
  classification: DeviationClassification;
  sourceArea: SourceArea;
  status: DeviationStatus;
  occurredAtUtc: string;
  reportedAtUtc: string;
  slaDueUtc: string | null;
  reporterUserId: string;
  investigatorUserId: string | null;
  requiresCapa: boolean;
  affectedEquipmentCount: number;
  affectedDocCount: number;
  affectedBatchCount: number;
  slaBreached: boolean;
}

export interface DeviationsResponse {
  items: DeviationListItem[];
  total: number;
}

export interface DeviationStatsResponse {
  total: number;
  open: number;
  critical: number;
  awaitingTriage: number;
  slaBreached: number;
}

export interface DeviationsQueryParams {
  status?: DeviationStatus;
  classification?: DeviationClassification;
  sourceArea?: SourceArea;
}

export function useDeviationsQuery(
  params: DeviationsQueryParams,
): UseQueryResult<DeviationsResponse> {
  return useQuery<DeviationsResponse>({
    queryKey: ['deviations', 'queue', params],
    queryFn: ({ signal }) =>
      apiFetch<DeviationsResponse>('/api/v1/deviations', {
        query: {
          status: params.status,
          classification: params.classification,
          sourceArea: params.sourceArea,
        },
        signal,
      }),
  });
}

export function useDeviationStatsQuery(): UseQueryResult<DeviationStatsResponse> {
  return useQuery<DeviationStatsResponse>({
    queryKey: ['deviations', 'stats'],
    queryFn: ({ signal }) =>
      apiFetch<DeviationStatsResponse>('/api/v1/deviations/stats', { signal }),
  });
}
